<template>
  <div class="preview-page">
    <div v-if="isLoading" class="preview-shell" aria-busy="true">
      <div class="skeleton skeleton-title" />
      <div class="skeleton skeleton-line" />
      <div class="skeleton skeleton-line" />
      <div class="skeleton skeleton-line short" />
      <span class="tw-sr-only">Loading topic…</span>
    </div>

    <div v-else class="preview-shell">
      <nav class="breadcrumb" aria-label="Breadcrumb">
        <ol>
          <li><router-link to="/">Home</router-link></li>
          <li aria-hidden="true" class="sep">/</li>
          <li><span class="current">{{ title }}</span></li>
        </ol>
      </nav>

      <article class="topic">
        <header class="topic-head">
          <h1>{{ title }}</h1>
          <p class="topic-meta">
            <span>{{ readingMinutes }} min read</span>
            <span aria-hidden="true" class="dot">·</span>
            <span>Evidence-linked topic</span>
            <span aria-hidden="true" class="dot">·</span>
            <span>Green Medicine Encyclopedia</span>
          </p>
        </header>

        <!--
          `.seo-preview` is styled globally in public/style.css (Subheadinghdk,
          hdkbullet1, bullet_green …) because the markup comes from the API.
          `.is-gated` fades the final lines so the cut reads as a deliberate
          preview rather than a page that failed to load.
        -->
        <div
          ref="bodyEl"
          class="seo-preview"
          :class="{ 'is-gated': isTruncated }"
          v-html="seoData"
          @click="onBodyClick"
        />

        <section v-if="isTruncated" class="paywall-gate">
          <div class="gate-progress" role="img" :aria-label="progressLabel">
            <div class="gate-progress-bar">
              <span :style="{ width: `${previewPercent}%` }" />
            </div>
            <p class="gate-progress-text">{{ progressLabel }}</p>
          </div>

          <h2 class="gate-title">Keep reading {{ title }}</h2>

          <ul class="gate-list">
            <li v-for="item in unlockList" :key="item">
              <CheckIcon class="gate-check" aria-hidden="true" />
              <span>{{ item }}</span>
            </li>
          </ul>

          <div class="gate-actions">
            <router-link
              to="/register/trial"
              class="btn btn-primary"
              @click="onCta('gate', '/register/trial', 'Start free trial')"
            >
              Start your free trial
            </router-link>
            <router-link
              to="/plans"
              class="btn btn-secondary"
              @click="onCta('gate', '/plans', 'See plans')"
            >
              See plans
            </router-link>
          </div>

          <p class="gate-note">
            No credit card needed to start a trial. Already a member?
            <router-link
              to="/auth/login"
              @click="onCta('gate', '/auth/login', 'Log in')"
            >
              Log in
            </router-link>
          </p>
        </section>

        <!--
          Short topics are published whole, so there is nothing to gate. They
          still need an exit toward signup — without one the reader finishes and
          leaves.
        -->
        <section v-else class="soft-cta">
          <p>
            This is one of more than 5,000 interlinked topics in the Green
            Medicine Encyclopedia.
          </p>
          <router-link
            to="/register/trial"
            class="btn btn-primary"
            @click="onCta('soft', '/register/trial', 'Start free trial')"
          >
            Explore the full encyclopedia free
          </router-link>
        </section>
      </article>

      <section v-if="relatedTopics.length" class="related" aria-labelledby="related-h">
        <h2 id="related-h">Related topics</h2>
        <ul class="related-grid">
          <li v-for="(topic, index) in visibleRelated" :key="topic.id">
            <router-link
              :to="`/preview/${topic.id}`"
              @click="onRelated(topic, index)"
            >
              {{ topic.title }}
            </router-link>
          </li>
        </ul>
        <button
          v-if="relatedTopics.length > relatedLimit"
          type="button"
          class="related-more"
          @click="showAllRelated = !showAllRelated"
        >
          {{
            showAllRelated
              ? "Show fewer"
              : `Show all ${relatedTopics.length} related topics`
          }}
        </button>
      </section>
    </div>

    <!--
      Sticky bar for small screens. On mobile the gate is often well below the
      fold, so the only call to action can scroll out of reach entirely.
    -->
    <div v-if="!isLoading && isTruncated" class="sticky-cta">
      <span>Reading {{ previewPercent }}% of this topic</span>
      <router-link
        to="/register/trial"
        class="btn btn-primary btn-sm"
        @click="onCta('sticky', '/register/trial', 'Start free trial')"
      >
        Unlock it free
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSeo } from "@/composables/useSeo";
import { useAnalytics } from "@/composables/useAnalytics";
import { usePreviewStore, type RelatedTopic } from "@/stores/preview";
import { useToastStore } from "@/stores/toast";
import { useHead } from "@unhead/vue";
import { useRoute, useRouter } from "vue-router";
import { CheckIcon } from "@heroicons/vue/20/solid";
import DOMPurify from "dompurify";

const route = useRoute();
const router = useRouter();
const previewStore = usePreviewStore();
const toastStore = useToastStore();
const analytics = useAnalytics();
const siteBaseUrl = "https://www.greenmedicineworks.com";

const id = computed(() => (route.params as { id: string }).id);
const entry = computed(() => previewStore.cache[id.value]);

const isLoading = computed(() => !entry.value);
const title = computed(() => entry.value?.title ?? "");
const isTruncated = computed(() => entry.value?.isTruncated ?? false);
const previewPercent = computed(() => entry.value?.previewPercent ?? 100);
const readingMinutes = computed(() => entry.value?.readingMinutes ?? 1);
const relatedTopics = computed(() => entry.value?.relatedTopics ?? []);

// Sanitize at the render boundary (client only — the server has no DOM and
// bakes the raw first-party HTML for crawlers; the client re-renders, so the
// sanitized value cleanly replaces it).
const seoData = computed(() => {
  const html = entry.value?.seoData ?? "";
  return typeof window !== "undefined" ? DOMPurify.sanitize(html) : html;
});

const progressLabel = computed(
  () => `You have read ${previewPercent.value}% of this topic`
);

/**
 * What a subscription unlocks, named from this specific article.
 *
 * The server reports the withheld section headings, so the gate can promise
 * "Nutrient composition table" instead of a generic "subscribe to continue".
 * Concrete, article-specific promises are what make a gate convert; the
 * generic lines below only fill in when the server has nothing to name.
 */
const unlockList = computed(() => {
  const e = entry.value;
  if (!e) return [];

  const items = [...e.lockedSections];

  if (e.strippedImages > 0) {
    items.push(
      e.strippedImages === 1
        ? "Full-size illustration"
        : `${e.strippedImages} full-size illustrations`
    );
  }

  const remaining = e.lockedSectionCount - e.lockedSections.length;
  if (remaining > 0) {
    items.push(`${remaining} more section${remaining === 1 ? "" : "s"}`);
  }

  items.push("Every cited reference behind this topic");
  items.push("All 5,000+ interlinked topics, search and notes");

  return items;
});

const relatedLimit = 12;
const showAllRelated = ref(false);
const visibleRelated = computed(() =>
  showAllRelated.value
    ? relatedTopics.value
    : relatedTopics.value.slice(0, relatedLimit)
);

const analyticsContext = computed(() => ({
  topicId: id.value,
  topicTitle: title.value,
}));

// SEO meta + canonical. Getter form so the head resolves the fetched values
// when the prerender snapshot is taken (after onServerPrefetch) and again
// after any client-side fetch.
useSeo(() => {
  const e = entry.value;
  return {
    title: e?.seo.title,
    description: e?.seo.description,
    keywords: e?.seo.keywords,
    ogtitle: e?.seo.ogtitle,
    ogdescription: e?.seo.ogdescription,
    url: `${siteBaseUrl}/preview/${id.value}`,
  };
});

// JSON-LD baked into the prerendered <head>: the topic itself (including the
// paywall declaration built in the store) plus a breadcrumb, which is what
// lets the result show a path rather than a bare URL.
useHead(
  computed(() => {
    const e = entry.value;
    if (!e?.jsonLd) return { script: [] };

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteBaseUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: e.title,
          item: `${siteBaseUrl}/preview/${id.value}`,
        },
      ],
    };

    return {
      script: [
        { type: "application/ld+json", innerHTML: JSON.stringify(e.jsonLd) },
        { type: "application/ld+json", innerHTML: JSON.stringify(breadcrumb) },
      ],
    };
  })
);

// Server: prefetch so content + head + JSON-LD are baked into the static HTML.
onServerPrefetch(async () => {
  await previewStore.load(id.value);
});

async function ensureLoaded(targetId: string) {
  const e = await previewStore.load(targetId);
  if (!e.found && e.error) {
    toastStore.error(e.error);
    router.replace({
      path: "/error",
      query: { error: e.error, title: "Error on getting content" },
    });
  }
}

function reportView() {
  const e = entry.value;
  if (!e?.found) return;
  analytics.previewView(analyticsContext.value, {
    isPaywalled: e.isTruncated,
    previewPercent: e.previewPercent,
    lockedSections: e.lockedSectionCount,
    relatedTopics: e.relatedTopics.length,
    readingMinutes: e.readingMinutes,
  });
}

function onCta(placement: string, target: string, label: string) {
  analytics.previewCtaClick(analyticsContext.value, {
    placement,
    target,
    label,
  });
}

function onRelated(topic: RelatedTopic, index: number) {
  analytics.relatedTopicClick(analyticsContext.value, {
    toTopicId: topic.id,
    toTopicTitle: topic.title,
    position: index + 1,
  });
}

/**
 * Keep in-body cross-links inside the SPA.
 *
 * The article HTML arrives from the API with `<a href="/preview/1763">` anchors
 * that the server rewrote from bare content ids. Left alone each one is a full
 * page reload — re-downloading the bundle to move between two topics. Handled
 * here by delegation because the markup is injected with v-html, so there is no
 * component to bind a listener to.
 */
function onBodyClick(event: MouseEvent) {
  // Let modified clicks (new tab, download, context menu) behave natively.
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const anchor = (event.target as HTMLElement | null)?.closest("a");
  if (!anchor) return;
  if (anchor.target && anchor.target !== "_self") return;

  const href = anchor.getAttribute("href");
  const match = href?.match(/^\/preview\/(\d+)$/);
  if (!match) return;

  event.preventDefault();
  analytics.relatedTopicClick(analyticsContext.value, {
    toTopicId: match[1],
    toTopicTitle: anchor.textContent?.trim() ?? "",
    position: 0,
  });
  router.push(href!);
}

/**
 * Scroll-depth reporting, at quarter marks.
 *
 * This is what shows whether readers reach the gate at all. If preview_view is
 * high but the 75% mark is rare, the problem is the article, not the offer.
 */
const reachedDepths = new Set<number>();
let depthFrame = 0;

function measureDepth() {
  depthFrame = 0;
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return;

  const percent = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
  for (const mark of [25, 50, 75, 100]) {
    if (percent >= mark && !reachedDepths.has(mark)) {
      reachedDepths.add(mark);
      analytics.previewScrollDepth(analyticsContext.value, mark);
    }
  }
}

function onScroll() {
  if (depthFrame) return;
  depthFrame = window.requestAnimationFrame(measureDepth);
}

onMounted(() => {
  if (!entry.value) {
    ensureLoaded(id.value).then(reportView);
  } else {
    reportView();
  }
  window.addEventListener("scroll", onScroll, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
  if (depthFrame) window.cancelAnimationFrame(depthFrame);
});

// Client-side navigation between preview ids.
watch(id, async (newId) => {
  if (!newId) return;
  reachedDepths.clear();
  showAllRelated.value = false;
  if (!previewStore.cache[newId]) await ensureLoaded(newId);
  reportView();
});
</script>

<style scoped>
.preview-page {
  background-color: #eaeed3;
  min-height: 100vh;
  /* Room for the sticky bar so it never covers the last lines of the page. */
  padding-bottom: 5.5rem;
}

@media (min-width: 640px) {
  .preview-page {
    padding-bottom: 0;
  }
}

.preview-shell {
  max-width: 52rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 3rem;
}

/* ---------- breadcrumb ---------- */

.breadcrumb ol {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  margin: 0 0 1rem;
  list-style: none;
  font-size: 0.875rem;
}

.breadcrumb a {
  color: #346a7e;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.breadcrumb .sep {
  color: #8aa3ad;
}

.breadcrumb .current {
  color: #5c7681;
}

/* ---------- article ---------- */

.topic {
  background: #fff;
  border-radius: 1rem;
  padding: 2rem 1.5rem;
  box-shadow: 0 1px 3px rgba(35, 77, 91, 0.08),
    0 8px 24px rgba(35, 77, 91, 0.06);
}

@media (min-width: 640px) {
  .topic {
    padding: 2.5rem 3rem;
  }
}

.topic-head {
  border-bottom: 1px solid rgba(52, 106, 126, 0.15);
  padding-bottom: 1.25rem;
  margin-bottom: 1.5rem;
}

.topic-head h1 {
  font-family: "Roboto Condensed", sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.75rem);
  font-weight: 700;
  line-height: 1.15;
  color: #234d5b;
  margin: 0;
}

.topic-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
  color: #5c7681;
}

.topic-meta .dot {
  color: #a8bcc4;
}

/* ---------- API-authored body ---------- */

/*
  Deep selectors: this markup is injected with v-html, so scoped styles do not
  reach it on their own. The base classes (Subheadinghdk, hdkbullet1, bullet_*)
  are styled globally in public/style.css; what is set here is the reading
  layout that file does not provide — measure, rhythm and link treatment.
*/
.seo-preview {
  color: #2f4a54;
  font-size: 1.0625rem;
  line-height: 1.7;
}

.seo-preview :deep(p) {
  margin: 0 0 0.85rem;
}

.seo-preview :deep(.Subheadinghdk) {
  color: #234d5b;
  font-family: "Roboto Condensed", sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  margin-top: 2rem;
}

.seo-preview :deep(.hdksubsubheading) {
  color: #2d6f41;
  font-weight: 700;
  font-size: 1.0625rem;
  margin-top: 1.25rem;
}

.seo-preview :deep(a.data) {
  color: #2d6f41;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.seo-preview :deep(a.data:hover) {
  color: #1a4d2e;
}

/* Inert footnote markers — the server strips their hrefs, since the reference
   list itself is subscriber content. */
.seo-preview :deep(.ref-marker) {
  color: #7a949e;
  font-size: 0.7em;
  vertical-align: super;
  margin-left: 1px;
}

.seo-preview :deep(img) {
  max-width: 100%;
  height: auto;
}

/* Wide Word-exported tables must scroll inside the article, never widen it. */
.seo-preview :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
}

/* Fade the tail of a gated article into the gate below it. */
.seo-preview.is-gated {
  position: relative;
  mask-image: linear-gradient(to bottom, #000 calc(100% - 5rem), transparent);
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 calc(100% - 5rem),
    transparent
  );
}

/* ---------- paywall gate ---------- */

.paywall-gate {
  margin-top: 1rem;
  border: 1px solid rgba(45, 111, 65, 0.25);
  border-radius: 0.875rem;
  background: linear-gradient(180deg, #f6f9ee 0%, #eef5e6 100%);
  padding: 1.75rem 1.5rem;
}

@media (min-width: 640px) {
  .paywall-gate {
    padding: 2rem;
  }
}

.gate-progress-bar {
  height: 6px;
  border-radius: 999px;
  background: rgba(45, 111, 65, 0.15);
  overflow: hidden;
}

.gate-progress-bar span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #2d6f41;
}

.gate-progress-text {
  margin: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: #4a6b56;
}

.gate-title {
  font-family: "Roboto Condensed", sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a4d2e;
  margin: 1.25rem 0 0.875rem;
}

.gate-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  display: grid;
  gap: 0.5rem;
}

.gate-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: #2f4a3a;
}

.gate-check {
  width: 1.125rem;
  height: 1.125rem;
  flex: none;
  margin-top: 0.15rem;
  color: #2d6f41;
}

.gate-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.gate-note {
  margin: 1rem 0 0;
  font-size: 0.8125rem;
  color: #5c7681;
}

.gate-note a {
  color: #2d6f41;
  font-weight: 500;
}

/* ---------- buttons ---------- */

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.75rem 1.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.btn-primary {
  background: #2d6f41;
  color: #fff;
  box-shadow: 0 1px 2px rgba(26, 77, 46, 0.3);
}

.btn-primary:hover {
  background: #245a35;
}

.btn-secondary {
  background: #fff;
  color: #2d6f41;
  border: 1px solid rgba(45, 111, 65, 0.4);
}

.btn-secondary:hover {
  border-color: #2d6f41;
}

.btn-sm {
  padding: 0.55rem 1.1rem;
  font-size: 0.875rem;
}

/* ---------- soft CTA (ungated topics) ---------- */

.soft-cta {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(52, 106, 126, 0.15);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.soft-cta p {
  margin: 0;
  color: #4a6b78;
  font-size: 0.9375rem;
  max-width: 26rem;
}

/* ---------- related topics ---------- */

.related {
  margin-top: 2.5rem;
}

.related h2 {
  font-family: "Roboto Condensed", sans-serif;
  font-size: 1.375rem;
  font-weight: 700;
  color: #234d5b;
  margin: 0 0 1rem;
}

.related-grid {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.625rem;
  grid-template-columns: 1fr;
}

@media (min-width: 640px) {
  .related-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 900px) {
  .related-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.related-grid a {
  display: block;
  background: #fff;
  border: 1px solid rgba(52, 106, 126, 0.15);
  border-radius: 0.625rem;
  padding: 0.75rem 0.9rem;
  color: #234d5b;
  font-size: 0.9375rem;
  text-decoration: none;
  transition: border-color 0.15s ease, transform 0.15s ease;
}

.related-grid a:hover {
  border-color: #2d6f41;
  color: #1a4d2e;
  transform: translateY(-1px);
}

.related-more {
  margin-top: 0.875rem;
  background: none;
  border: none;
  padding: 0;
  color: #2d6f41;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

/* ---------- sticky mobile CTA ---------- */

.sticky-cta {
  position: fixed;
  inset: auto 0 0 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
  /* Opaque: article text scrolling underneath a translucent bar reads as a
     rendering fault, and it makes the button label harder to pick out. */
  background: #fff;
  border-top: 1px solid rgba(52, 106, 126, 0.2);
  box-shadow: 0 -4px 16px rgba(35, 77, 91, 0.1);
  font-size: 0.8125rem;
  color: #4a6b78;
}

@media (min-width: 640px) {
  .sticky-cta {
    display: none;
  }
}

/* ---------- loading skeleton ---------- */

.skeleton {
  background: linear-gradient(90deg, #e4e8cd 25%, #eef1e0 50%, #e4e8cd 75%);
  background-size: 200% 100%;
  border-radius: 0.5rem;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
}

.skeleton-title {
  height: 2.5rem;
  width: 60%;
  margin-bottom: 1.5rem;
}

.skeleton-line {
  height: 1rem;
  margin-bottom: 0.75rem;
}

.skeleton-line.short {
  width: 70%;
}

@keyframes skeleton-shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
  }
  .related-grid a:hover {
    transform: none;
  }
}
</style>

<!--
  `public`, not `public.vue`. vite-plugin-vue-layouts registers layouts under a
  key with the extension stripped, so `layout: public.vue` resolved to
  undefined and the layout was dropped without warning — which is why these
  pages rendered with no header or footer.
-->
<route lang="yaml">
meta:
  layout: public
</route>
